import {skipToken} from '@reduxjs/toolkit/query';

import {useRef} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  useGetVehicleFinancingTemplateQuery,
  useLazyGetVehicleFinancingTemplateQuery,
  useListVehicleFinancingTemplateQuery,
} from '@dms/api/sales';
import {SaveSaleVehicleFinancingBody, VehicleFinancingType} from '@dms/api/vehicle';
import {handleApiError} from '@dms/shared';

import {FinancingDrafts} from '../types/FinancingDrafts';
import {FinancingTemplateDraft} from '../types/FinancingTemplateDraft';
import {FinancingTemplateSelection} from '../types/FinancingTemplateSelection';
import {buildFinancingTemplateOptions} from '../utils/buildFinancingTemplateOptions';
import {applyFinancingTemplate} from '../utils/financingBody/applyFinancingTemplate';
import {rebaseFinancingTemplate} from '../utils/financingBody/rebaseFinancingTemplate';
import {normalizeVehicleFinancingTemplateForEditing} from '../utils/normalizeVehicleFinancingTemplateForEditing';

interface UseFinancingTemplateSelectionParams {
  canReadTemplates: boolean;
  activeType: VehicleFinancingType;
  vehicleFinancingBody: SaveSaleVehicleFinancingBody | null;
  getDrafts: () => FinancingDrafts;
  getPersistedBody: (type: VehicleFinancingType) => SaveSaleVehicleFinancingBody | null;
  applyDraft: (
    type: VehicleFinancingType,
    vehicleFinancingBody: SaveSaleVehicleFinancingBody
  ) => void;
  onActiveTypeChange: (type: VehicleFinancingType) => void;
  onTemplateApplied: (
    type: VehicleFinancingType,
    appliedBody: SaveSaleVehicleFinancingBody
  ) => void;
  onTemplateRebased: () => void;
  onSelectionCleared: () => void;
}

/**
 * The template side of the financing panel: which templates it offers, which one the financing
 * follows, and what applying, re-applying or dropping a template does to the draft.
 */
export function useFinancingTemplateSelection(params: UseFinancingTemplateSelectionParams) {
  const templateRequestIdRef = useRef(0);
  const {
    data: templates,
    isLoading: areTemplatesLoading,
    isError: areTemplatesError,
  } = useListVehicleFinancingTemplateQuery(params.canReadTemplates ? undefined : skipToken);
  const selectedTemplateId =
    params.vehicleFinancingBody?.mode === 'TEMPLATE'
      ? params.vehicleFinancingBody.templateId
      : null;
  const selectedTemplateQuery = useGetVehicleFinancingTemplateQuery(
    params.canReadTemplates && isNotNil(selectedTemplateId)
      ? {vehicleFinancingTemplateId: selectedTemplateId}
      : skipToken
  );
  const [loadTemplate, templateApplicationStatus] = useLazyGetVehicleFinancingTemplateQuery();
  const selectedTemplate =
    params.vehicleFinancingBody?.mode === 'TEMPLATE' &&
    selectedTemplateQuery.currentData?.id === selectedTemplateId
      ? normalizeVehicleFinancingTemplateForEditing(selectedTemplateQuery.currentData)
      : undefined;

  const applyLoadedTemplate = (template: FinancingTemplateDraft) => {
    const currentTemplateBody =
      params.getDrafts()[template.type] ?? params.getPersistedBody(template.type);
    const appliedBody = applyFinancingTemplate(template, currentTemplateBody);

    params.applyDraft(template.type, appliedBody);
    params.onTemplateApplied(template.type, appliedBody);
  };

  /** Reports a failed load itself, so every caller can treat the applied template as best effort. */
  const applyTemplateSelection = (templateId: string) => {
    const requestId = templateRequestIdRef.current + 1;
    templateRequestIdRef.current = requestId;

    // `preferCacheValue` — applying a template must not refetch one the panel already holds.
    return loadTemplate({vehicleFinancingTemplateId: templateId}, true)
      .unwrap()
      .then((template) => {
        if (requestId === templateRequestIdRef.current) {
          applyLoadedTemplate(template);
        }
      })
      .catch(handleApiError);
  };

  const handleTemplateCreated = (template: FinancingTemplateSelection) => {
    params.onActiveTypeChange(template.type);
    return applyTemplateSelection(template.id);
  };

  const handleTemplateUpdated = (updatedTemplate: FinancingTemplateDraft) => {
    if (
      selectedTemplate === undefined ||
      selectedTemplate.id !== updatedTemplate.id ||
      selectedTemplate.type !== updatedTemplate.type
    ) {
      return;
    }

    const currentBody =
      params.getDrafts()[updatedTemplate.type] ?? params.getPersistedBody(updatedTemplate.type);

    if (currentBody) {
      params.applyDraft(
        updatedTemplate.type,
        rebaseFinancingTemplate(updatedTemplate, selectedTemplate, currentBody)
      );
    }
    params.onTemplateRebased();
  };

  const handleTemplateChange = (templateId: string | null) => {
    if (templateId) {
      void applyTemplateSelection(templateId);
      return;
    }

    discardPendingApplication();

    if (isNil(selectedTemplateId)) {
      return;
    }

    params.onSelectionCleared();
  };

  /** Keeps a template loaded for the type the user just left from landing on the one they opened. */
  function discardPendingApplication() {
    templateRequestIdRef.current += 1;
  }

  return {
    templateOptions: buildFinancingTemplateOptions(
      templates ?? [],
      params.activeType,
      selectedTemplateId
    ),
    selectedTemplateId,
    selectedTemplate,
    areTemplatesLoading,
    areTemplatesError,
    isTemplateLoading: selectedTemplateQuery.isLoading || selectedTemplateQuery.isFetching,
    isTemplateError: selectedTemplateQuery.isError,
    isApplyingTemplate: templateApplicationStatus.isFetching,
    discardPendingApplication,
    handleTemplateChange,
    handleTemplateCreated,
    handleTemplateUpdated,
  };
}
